<template>
  <PortalWidget :widget="widget">
  <div class="rtb-stats__widget">
    <template>
      <LoadingStatusDisplay v-if="isLoading" />
      <div
        v-else-if="!rateLimitData.length"
        class="empty-records"
      >
        {{ $t('RATE_LIMIT_PANE_EMPTY') }}
      </div>
    <v-table v-else
      :data="rateLimitData"
      :columns="columns"
      class="table_beauty"
    >
      <template #head>
        <thead>
          <tr>
            <th
              v-for="(col, index) in columns"
              :key="col.key + '_' + index"
              :class="col.headerClass"
            >
              {{ $t(col.displayName) }}
            </th>
          </tr>
        </thead>
      </template>

      <template #body="{ displayData }">
        <tbody>
          <template v-for="(row, index) in displayData">
            <!-- Main Row -->
            <tr
              :key="'main_' + index"
              :class="['main-row', getRowClass(row, index, displayData), { selected: expandedRow === index }]"
              @click="toggleDetails(index)"
              :aria-expanded="expandedRow === index"
              @keydown.enter="toggleDetails(index)"
              @keydown.space="toggleDetails(index)"
            >
              <td
                v-for="(col, colIndex) in columns"
                :key="col.key + '_' + colIndex + '_' + index"
                :class="col.cellClass"
              >
                <span v-if="colIndex===0" class="caret" :class="{ up: expandedRow === index }"></span>
                <slot
                  :name="
                    $scopedSlots[`col:${colIndex + 1}`]
                      ? `col:${colIndex + 1}`
                      : 'cell'
                  "
                  v-bind="{
                    row,
                    index,
                    col,
                    colIndex,
                    value: col.valueFormatter
                      ? col.valueFormatter({ value: row[col.key] })
                      : row[col.key]
                  }"
                >
                  {{
                    col.valueFormatter
                      ? col.valueFormatter({ value: row[col.key] })
                      : row[col.key]
                  }}
                </slot>
              </td>
            </tr>

            <!-- Expandable Row -->
            <tr
              v-if="expandedRow === index"
              :key="'expanded_' + index"
              class="expanded-row noHover"
            >
              <td :colspan="columns.length" class="expanded-content no-padding">
                <v-table
                  :data="extraData"
                  :columns="extraColumns"
                  class="table_beauty"
                >
                  <template #head>
                    <thead>
                      <tr>
                        <th
                          v-for="(col, index) in extraColumns"
                          :key="col.key + '_' + index + '_extraData'"
                          :class="col.headerClass"
                        >
                          {{ $t(col.displayName) }}
                        </th>
                      </tr>
                    </thead>
                  </template>

                  <template #body="{ displayData }">
                    <tbody>
                      <tr
                        v-for="(extraRow, extraIndex) in displayData"
                        :key="extraIndex + '_extraData'"
                      >
                        <td
                          v-for="(col, colIndex) in extraColumns"
                          :key="col.key + '_' + colIndex + '_' + extraIndex + '_extraData'"
                          :class="col.cellClass" style="white-space: nowrap;"
                        >
                          {{
                            col.valueFormatter
                              ? col.valueFormatter({ value: extraRow[col.key] })
                              : extraRow[col.key]
                          }}
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-table>
              </td>
            </tr>
          </template>
        </tbody>
      </template>
    </v-table>
    </template>
  </div>
  </PortalWidget>
</template>

<script>
import SmartTableWidget from '@/core/components/SmartTable/SmartTableWidget.vue';
import LoadingStatusDisplay from '@/core/components/Loader/LoadingStatusDisplay.vue';
import PortalWidget from '@/core/components/PortalWidget.vue';

import sharedMessages from '@/core/locales';
import messages from '../locales';

import { mapActions, mapGetters, mapState } from 'vuex';

import { STATUS_SUCCESS, STATUS_PENDING } from '@/store/helpers';

import { rateLimitsColumnSettings, extraColumnSettings } from '../columns';
export default {
  components: { SmartTableWidget, LoadingStatusDisplay, PortalWidget, TransitionComp: {
      template: '<div><slot></slot></div>'
    }},
  data() {
    return {
      rateLimitData: [],
      extraData: [],
      defaultColumns: rateLimitsColumnSettings,
      defaultExtraColumns: extraColumnSettings,
      sliceIndex: 0,
      expandedRow: null,
      rateLimitsForTable: [],
      widget:{title:this.$i18n.t('RATE_LIMITS_PAGE_TITLE'), iconClass: 'typcn typcn-user'}
    };
  },
  computed: {
    ...mapGetters('rateLimits', ['getRateLimitStatus']),
    ...mapState('rateLimits', { limits: 'rateLimits' }),
    isLoading() {
      return this.getRateLimitStatus.status === STATUS_PENDING;
    },
    columns() {
      if (this.rateLimitData.length) {
        return Array.from(
          this.defaultColumns || Object.keys(this.rateLimitData[0]),
          k => {
            if (k?.hide) {
              return;
            }

            if (typeof k !== 'string') {
              /** @type {import('@ag-grid-community/core').ColDef} */
              const {
                field,
                headerName,
                colId,

                /** @type {("asc"|"desc"|null)[]} */
                sortingOrder,
                valueFormatter,
                headerClass,
                cellClass,
                tooltipText
              } = k;
              return {
                key: field || colId,
                displayName: headerName,
                name: field || colId,
                defaultSort: sortingOrder && sortingOrder[0],
                valueFormatter,
                headerClass,
                cellClass,
                tooltipText
              };
            }

            return {
              key: k,
              displayName: k,
              name: k
            };
          }
        ).filter(v => !!v);
      }

      return null;
    },
    extraColumns() {
      if (this.extraData.length) {
        return Array.from(
          this.defaultExtraColumns || Object.keys(this.extraData[0]),
          k => {
            if (k?.hide) {
              return;
            }

            if (typeof k !== 'string') {
              /** @type {import('@ag-grid-community/core').ColDef} */
              const {
                field,
                headerName,
                colId,

                /** @type {("asc"|"desc"|null)[]} */
                sortingOrder,
                valueFormatter,
                headerClass,
                cellClass,
                tooltipText
              } = k;
              return {
                key: field || colId,
                displayName: headerName,
                name: field || colId,
                defaultSort: sortingOrder && sortingOrder[0],
                valueFormatter,
                headerClass,
                cellClass,
                tooltipText
              };
            }

            return {
              key: k,
              displayName: k,
              name: k
            };
          }
        ).filter(v => !!v);
      }

      return null;
    },
  },
  watch: {
    getRateLimitStatus: {
      handler(newVal) {
        if (newVal.status === STATUS_SUCCESS) {
          this.setRateLimits();
        }
      },
      immediate: true,
    },
},
  methods: {
    ...mapActions('rateLimits', ['getRateLimitsByUser']),
    ...mapGetters('rateLimits', ['getRateLimitFormattedData']),
    async fetchRateLimits() {
      this.getRateLimitsByUser();
    },
    setRateLimits() {
      this.rateLimitData = this.getRateLimitFormattedData();
    },
    getRowClass(_row, index, displayData) {
      return { 'bg-row-odd': index % 2 === 0, 'bg-row-even': index % 2 !== 0 };
    },
    toggleDetails(index) {
      const rateLimitForIndex = this.rateLimitData[index];
      const extraDataForIndex = this.$store.state.rateLimits.rateLimits.find(item => {
        return item.email === rateLimitForIndex.rateLimitsEmail;
      });

      this.extraData = extraDataForIndex.rateLimits.map(item => {
        return {
          rateLimitsPathPattern: item.pathPattern,
          rateLimitsMethod: item.method,
          rateLimitsLimit: item.limit,
          rateLimitsPeriod: this.normalizeSecondsToHumanReadable(item.period),
        };
      });

      this.expandedRow = this.expandedRow === index ? null : index;
    },
    normalizeSecondsToHumanReadable(seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = seconds % 60;

      let result = [];

      if (hours > 0) result.push(`${hours}h`);
      if (minutes > 0) result.push(`${minutes}m`);
      if (remainingSeconds > 0 || result.length === 0) result.push(`${remainingSeconds}s`);

      return result.join(' ');
    }
  },
  created() {
    // Add your created lifecycle hook here
  },
  mounted() {
    this.sliceIndex = 1;
    this.fetchRateLimits();
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>

<style scoped>
.main-row {
  cursor: pointer;
}

.expanded-row td {
  padding: 10px;
  border-top: 1px;
}

.expanded-row .table_beauty {
  margin: 0;
  border: none;
}

.expanded-row .table_beauty th,
.expanded-row .table_beauty td {
  border: none;
  padding: 5px;
}

.selected {
  background-color: #161824 !important;
  color: white;
}

.caret {
  display: inline-block;
  margin-right: 5px;
  transition: transform 0.3s ease;
}

.caret.up {
  transform: rotate(180deg);
}

.noHover {
  pointer-events: none;
}

.expanded-content {
  padding: 0;
}

.expanded-content .table_beauty {
  width: 100%;
  table-layout: auto;
}

.expanded-content .table_beauty th,
.expanded-content .table_beauty td {
  white-space: nowrap;
  padding: 5px;
}

.expanded-content .table_beauty tr:hover {
  background-color: transparent;
  color: inherit;
  cursor: default;
}

.no-padding {
  padding: 0 !important;
}


</style>